/**
 * Who is actually on a template — derived from the assignment list, because the library read does
 * not carry it and is not going to.
 *
 * Owner decision **D11**, 3 September 2026: `GET /plan-template/library` stays untouched and keeps
 * carrying `assignedClientCount` and nothing else. `GET /plan-assignment` already returns every
 * assignment the trainer owns, each with its own `client: PersonRef` and the `sourceTemplateId` it
 * was forked from, so the field this round was going to add would have been a second spelling of a
 * fact the app can already read — at the cost of a Plans → Accounts boundary and a regenerated
 * client on the mobile app.
 *
 * **The library read is the COUNT; this is the single source of WHO.** The strip prints the count
 * from the list row, which arrives with the screen, and the faces from this map, which arrive with
 * the assignment read. The two are allowed to disagree for the length of one request and the strip
 * does not wait for them to agree — see `AssignedStrip`.
 *
 * ── Active and Paused, and the rule is `assignmentStatus`'s ──────────────────────────────────
 * The same two statuses the `Plany klientów` list is built from, because the count this strip
 * prints comes from the library row and that figure counts exactly that population. One module
 * answers it for both.
 *
 * ── One row per PERSON, not per assignment ───────────────────────────────────────────────────
 * A client can hold two live assignments forked from one template — a paused one they are coming
 * back to and an active one they are on — and the strip is a row of faces. Two of the same face is
 * a defect, so the map is distinct by `customerId` and the first assignment encountered wins. What
 * it wins is only the person's own identity, which both rows carry identically.
 *
 * ── The person is read off the wire, never composed ──────────────────────────────────────────
 * `entities/client`'s rule, and the reason for it: the backend composes the display name (falling
 * back to the e-mail address when a client has set none), draws the initials from it, and resolves
 * the photo. Composing any of the three again is what made one client read `AK` in one app and
 * `AM` in another. An empty name and empty initials are therefore real answers here, not
 * placeholders to fill in.
 *
 * No React, no DOM, no query hook, no i18next. The COLLATOR's locale arrives as a parameter: a
 * rule that reads the ambient language cannot be tested against a second one.
 */

import type {
  TrainWithMeModulesAccountsDtosPersonRef as PersonRef,
  TrainWithMeModulesTrainingDtosResponsesPlanAssignmentSummary as PlanAssignmentSummary,
} from '~/shared/api/generated/model';

import { liveAssignmentStatus } from '~/entities/plan-draft';
import { textOrNull, type TranslateKey } from './planLibraryRow';

/** One follower, as the strip and its expanded table draw them. */
export interface PersonView {
  readonly customerId: number;
  /** As the backend composed it. Empty when the assignment carries no person at all. */
  readonly fullName: string;
  /** Up to two letters, as the backend drew them. May be empty; the chip then draws nothing. */
  readonly initials: string;
  readonly avatarUrl: string | null;
}

/**
 * Read a person off an assignment.
 *
 * Exported on its second consumer: `clientPlanRow` projects the SAME `client: PersonRef` from the
 * SAME endpoint, and two readings of one field is how a client came to read `AK` on one surface and
 * `AM` on another. Nothing outside `model/` sees it — the slice's `index.ts` does not carry it.
 */
export function personView(customerId: number, person: PersonRef | null): PersonView {
  return {
    customerId,
    fullName: textOrNull(person?.fullName) ?? '',
    initials: textOrNull(person?.initials) ?? '',
    avatarUrl: textOrNull(person?.avatarUrl),
  };
}

/**
 * What to draw where the backend composed no name at all.
 *
 * An empty `fullName` is a real answer — see the header — and every surface that prints a person
 * has to answer for it. The strip's expanded table, the strip's single-follower sentence and the
 * Klient facet each did so with their own copy of the same conditional, and the clients list's row
 * link did not: an assignment for a client with no name rendered an anchor with no accessible name
 * at all, which is a link a screen-reader user is told exists and cannot be told anything about.
 *
 * The KEY travels rather than the sentence, which is what keeps `model/` free of copy.
 */
export function personName(fullName: string, translate: TranslateKey): string {
  return fullName === '' ? translate('web:planLibrary.inspector.strip.unnamed') : fullName;
}

/**
 * The part of a name a Polish trainer alphabetises by.
 *
 * The wire carries ONE composed display name, so the surname is the last whitespace-separated word
 * of it. Exported for the same reason `personView` is: the clients scope alphabetises by the same
 * rule, and two spellings of "which word is the surname" would order the two lists differently.
 *
 * That is right for `Anna Kowalska` and for `Anna Maria Kowalska`, and it is the only rule
 * available without asking the backend to split a field it deliberately composes. A name with no
 * space is its own surname; an empty name sorts first, which is where an unnamed row belongs.
 */
export function surnameOf(fullName: string): string {
  const parts = fullName.split(/\s+/).filter(Boolean);
  return parts[parts.length - 1] ?? '';
}

/**
 * Group the trainer's live assignments by the template they were forked from.
 *
 * The returned lists are sorted by surname and then by the whole name, so two clients called
 * `Kowalska` keep a stable order rather than the one the backend happened to return them in.
 * `Intl.Collator` rather than `<`, because Polish orders `ą` after `a` and `ł` after `l`, and a
 * code-point comparison puts both after `z`.
 *
 * An assignment with no `sourceTemplateId` belongs to no template in the library and is dropped: it
 * is a plan built for one client directly, which the clients scope shows and this map does not.
 */
export function templateFollowers(
  assignments: readonly PlanAssignmentSummary[],
  locale: string,
): ReadonlyMap<number, readonly PersonView[]> {
  const byTemplate = new Map<number, Map<number, PersonView>>();

  for (const assignment of assignments) {
    if (liveAssignmentStatus(assignment.status) === null) continue;

    const templateId = assignment.sourceTemplateId;
    const customerId = assignment.customerId;
    if (templateId == null || customerId == null) continue;

    const followers = byTemplate.get(templateId) ?? new Map<number, PersonView>();
    // First assignment wins: a client holding two forks of one template is ONE face.
    if (!followers.has(customerId)) {
      followers.set(customerId, personView(customerId, assignment.client));
    }
    byTemplate.set(templateId, followers);
  }

  const collator = new Intl.Collator(locale, { sensitivity: 'base' });
  const sorted = new Map<number, readonly PersonView[]>();
  for (const [templateId, followers] of byTemplate) {
    sorted.set(
      templateId,
      [...followers.values()].sort(
        (a, b) =>
          collator.compare(surnameOf(a.fullName), surnameOf(b.fullName)) ||
          collator.compare(a.fullName, b.fullName),
      ),
    );
  }
  return sorted;
}
